/*
 * tw-recharge-overrides.js
 *
 * Overrides Recharge subscription widget display to match the store's
 * custom bundle pricing structure.
 *
 * What it does:
 *  - Strikethrough (subscription) → data-compare-at-display (Liquid: | money
 *    in default store currency, | money_with_currency in other Markets)
 *  - Strikethrough (one-time row)  → same compare above one-time when
 *    data-save-onetime is set (multi-quantity / bundle save)
 *  - Badge %            → stacked saving %     (10% / 14.5% / 17.2%)
 *  - Deliver every      → single-plan: title + styled span (Red Hat / 14px / brand text)
 *  - Tile badges        → switch between sub/onetime values on toggle
 *
 * Data source: data-* attributes on .quantity-save-badge elements,
 * output by main-product.liquid at render time — no Liquid needed here.
 *
 * Lit safety: uses nodeValue on specific text nodes instead of textContent
 * on parent elements. Setting textContent removes Lit's <!--?lit$-->
 * comment anchor nodes, causing "Cannot set properties of null (setting 'data')"
 * crashes inside Recharge's subscription-widget.js.
 */

(function () {
    'use strict';
  
    /* ─── Config ──────────────────────────────────────────────────────────── */
  
    const SEL = {
      widget:        'recharge-subscription-widget',
      rcBadge:       '#rc-subscription-badge',
      strikethrough: 'span.rc-price.strike-through',
      plans:         'rc-selling-plans',
      plansSelect:   'select.rc-plans-dropdown__select',
      plansLabel:    'span.rc-plans__label',
      plansIcon:     '.rc-plans-dropdown__icon',
      tileList:      '[class*="__list"]',
      variantInput:  'form[action*="/cart/add"] input[name="id"]',
      sellingPlan:   'form[action*="/cart/add"] input[name="selling_plan"]',
      tileBadge:     '.quantity-save-badge[data-option-index]',
      bundleWrap:    '[data-bundleapp-wrapper]',
      rcPurchaseOpt: 'input[name="purchaseOption"]:checked',
      bundlePlan:    'input[name="bundleapp-plan-selector-group"]:checked',
    };
  
    var COPY = {
      plansStyle:         'tw-plans-style',
      plansLabelValueStyle: 'tw-rc-plans-label-value-style',
      plansValueClass:    'tw-rc-plans-label-value',
      onetimePriceLayout: 'tw-rc-onetime-compare-style',
    };
  
    /* ─── State ───────────────────────────────────────────────────────────── */
  
    var _busy          = false;  // reentrance guard — prevents observer loops
    var _reapplyTimer  = null;
    var _observer      = null;
    var _scopedRoot    = null;   // quick-add wrapper while modal is open

    function getOverrideRoot() {
      return _scopedRoot || document;
    }

    /* ─── Utilities ───────────────────────────────────────────────────────── */
  
    /** Pierces into a nested custom element's shadow root */
    function nestedShadow(root, selector) {
      var el = root.querySelector(selector);
      return (el && el.shadowRoot) ? el.shadowRoot : null;
    }

    /** Recharge v2 parts use the `part` attribute; values can be a space list */
    function findByPart(root, partToken) {
      if (!root) return null;
      return (
        root.querySelector('[part~="' + partToken + '"]') || null
      );
    }

    function getPurchaseOptionByPart(shadow, partToken) {
      return findByPart(shadow, 'rc-purchase-option__' + partToken);
    }

    /**
     * Compare-at: Liquid `data-compare-at-display` (| money in default currency,
     * | money_with_currency in foreign markets). Else money + Intl fallbacks.
     */
    function formatCompareAtMoney(badge) {
      if (!badge) return null;
      var fromLiquid = badge.getAttribute('data-compare-at-display');
      if (fromLiquid != null && fromLiquid !== '') {
        return fromLiquid;
      }
      var pre = badge.getAttribute('data-full-price');
      var rawCents = badge.getAttribute('data-full-price-cents');
      if (rawCents == null || rawCents === '') return pre;
      var cents = parseInt(rawCents, 10);
      if (isNaN(cents)) return pre;
      var t = window.theme;
      var code = t && t.currency;
      if (code) {
        try {
          return new Intl.NumberFormat(
            t.locale || undefined,
            { style: 'currency', currency: code }
          ).format(cents / 100);
        } catch (e) {
        }
      }
      return pre;
    }

    function getOnetimeBlock(shadow) {
      var b = getPurchaseOptionByPart(shadow, 'onetime');
      if (b) return b;
      var i =
        shadow.querySelector('input[name="purchaseOption"][value="onetime"]') ||
        shadow.querySelector('input[name="purchaseOption"][value="oneTime"]');
      if (!i) {
        var all = shadow.querySelectorAll('input[name="purchaseOption"]');
        for (var k = 0; k < all.length; k++) {
          if ((all[k].value || '').toLowerCase() === 'onetime') {
            i = all[k];
            break;
          }
        }
      }
      if (!i) return null;
      var p = i.parentElement;
      var d = 0;
      while (p && p !== shadow && d < 8) {
        if (p.getAttribute && p.getAttribute('part')) {
          if (p.getAttribute('part').indexOf('onetime') !== -1) return p;
        }
        p = p.parentElement;
        d++;
      }
      return i.parentElement;
    }

    /** Injected or simple nodes: not managed by Lit */
    function setNonLitText(el, text) {
      if (!el) return;
      el.textContent = text == null || text === '' ? '' : String(text);
    }
  
    /**
     * Updates a Lit-managed text node safely using nodeValue.
     *
     * Lit stores a <!--?lit$--> comment node as its ChildPart anchor, with
     * the managed text node immediately after it. We update that text node's
     * nodeValue directly — no structural DOM changes, so Lit's reference
     * stays valid and never becomes null on its next render cycle.
     *
     * Guard: skips if the value hasn't changed to avoid firing unnecessary
     * characterData mutations (which would re-trigger the MutationObserver).
     */
    function setLitText(el, text) {
      if (!el || text == null) return;
      var nodes = el.childNodes;
  
      // Walk backwards: find last Lit comment marker, update the text node after it
      for (var i = nodes.length - 1; i >= 0; i--) {
        if (nodes[i].nodeType === 8 /* COMMENT_NODE */) {
          var next = nodes[i].nextSibling;
          if (next && next.nodeType === 3 /* TEXT_NODE */) {
            if (next.nodeValue !== text) next.nodeValue = text;
            return;
          }
          break;
        }
      }
  
      // Fallback: no Lit comment found — update last non-empty text node
      for (var j = nodes.length - 1; j >= 0; j--) {
        if (nodes[j].nodeType === 3 && nodes[j].nodeValue.trim()) {
          if (nodes[j].nodeValue !== text) nodes[j].nodeValue = text;
          return;
        }
      }
    }
  
    /** Returns the badge element for the currently checked quantity tile */
    function getActiveBadge() {
      var root = getOverrideRoot();
      var badges = root.querySelectorAll(SEL.tileBadge);
      for (var i = 0; i < badges.length; i++) {
        var label = badges[i].closest('label');
        if (!label) continue;
        var input =
          label.querySelector('input[type="radio"]') ||
          document.getElementById(label.getAttribute('for'));
        if (input && input.checked) return badges[i];
      }
      return badges.length ? badges[0] : null;
    }
  
    /** Returns true if the subscription purchase option is currently selected */
    function isSubscriptionSelected() {
      var root = getOverrideRoot();
      var sp = root.querySelector(SEL.sellingPlan);
      if (sp) return sp.value !== '';

      var bp = root.querySelector(SEL.bundlePlan);
      if (bp) return bp.value !== '';

      var widget = root.querySelector(SEL.widget);
      if (widget && widget.shadowRoot) {
        var po = widget.shadowRoot.querySelector(SEL.rcPurchaseOpt);
        if (po) return po.value === 'subscription';
      }
      return false;
    }
  
    /** Text after "Deliver every: " (single-option row) */
    function buildDeliverValueSuffix(interval, pct) {
      return (
        (interval || '') + (pct ? ' \u00b7 Save ' + pct + '%' : '')
      );
    }

    /**
     * Single-plan path: keep title in Lit text; value in a separate span
     * (typography in COPY.plansLabelValueStyle).
     */
    function setDeliverLabelWithStyledValue(pShadow, lbl, interval, savePct) {
      if (!lbl || !pShadow) return;
      if (!pShadow.querySelector('style.' + COPY.plansLabelValueStyle)) {
        var st = document.createElement('style');
        st.className = COPY.plansLabelValueStyle;
        st.textContent =
          'span.' +
          COPY.plansValueClass +
          '{font-family:"Red Hat Display",serif;font-weight:400;font-size:14px;' +
          'color:var(--color-brand-black,#262422);}';
        pShadow.appendChild(st);
      }
      setLitText(lbl, 'Deliver every: ');
      var suffix = buildDeliverValueSuffix(interval, savePct);
      var span = lbl.querySelector('span.' + COPY.plansValueClass);
      if (!span) {
        span = document.createElement('span');
        span.className = COPY.plansValueClass;
        lbl.appendChild(span);
      }
      span.textContent = suffix;
    }

    /** true when the tier has bundle savings for one-time (2+ units etc.) */
    function hasOnetimeStackSavings(badge) {
      if (!badge) return false;
      var raw = (badge.getAttribute('data-save-onetime') || '').trim();
      if (raw === '') return false;
      return parseFloat(raw) > 0;
    }

    function ensureOnetimeLayoutStyle(shadow) {
      if (shadow.querySelector('style.' + COPY.onetimePriceLayout)) return;
      var s = document.createElement('style');
      s.className = COPY.onetimePriceLayout;
      s.textContent =
        '[part~="rc-purchase-option__onetime"] [part~="rc-purchase-option__prices"]' +
        '{display:flex!important;flex-direction:column!important;align-items:flex-end!important' +
        ';gap:0.1em;justify-content:flex-end!important;}' +
        '[part~="rc-purchase-option__onetime"] span.rc-price.strike-through,' +
        'span.tw-rc-onetime-compare' +
        '{font-weight:400;white-space:nowrap;color:var(--rc-widget-brand-color-70);}';
      shadow.appendChild(s);
    }

    /**
     * Puts compare (full stack) above one-time when multi-qty save applies.
     */
    function applyOnetimeComparePrice(shadow, badge) {
      var onetimeRoot = getOnetimeBlock(shadow);
      if (!onetimeRoot) return;

      var prices = findByPart(onetimeRoot, 'rc-purchase-option__prices') ||
        onetimeRoot.querySelector('[part*="prices"]');
      if (!prices) return;

      var hasSave = hasOnetimeStackSavings(badge);
      var compareMoney = formatCompareAtMoney(badge);

      var fromRc =
        onetimeRoot.querySelector(SEL.strikethrough) ||
        findByPart(onetimeRoot, 'rc-purchase-option__original-price');
      if (fromRc) {
        var injected = fromRc.getAttribute('data-tw-injected') === '1';
        if (hasSave && compareMoney) {
          ensureOnetimeLayoutStyle(shadow);
          if (injected) {
            setNonLitText(fromRc, compareMoney);
          } else {
            setLitText(fromRc, compareMoney);
          }
          fromRc.style.display = '';
        } else {
          if (injected) {
            setNonLitText(fromRc, '');
          } else {
            setLitText(fromRc, '');
          }
          fromRc.style.display = 'none';
        }
        return;
      }

      var custom = prices.querySelector('span.tw-rc-onetime-compare');
      if (hasSave && compareMoney) {
        ensureOnetimeLayoutStyle(shadow);
        if (!custom) {
          custom = document.createElement('span');
          custom.className = 'rc-price strike-through tw-rc-onetime-compare';
          custom.setAttribute('data-tw-injected', '1');
          prices.insertBefore(custom, prices.firstChild);
        }
        setNonLitText(custom, compareMoney);
        custom.style.display = '';
      } else {
        if (custom) {
          setNonLitText(custom, '');
          custom.style.display = 'none';
        }
      }
    }
  
    /* ─── Tile badge updates ──────────────────────────────────────────────── */

    /**
     * Per-bottle + per-serving lines on every multibuy card.
     * bottle  = totalPrice / bottleCount
     * serving = totalPrice / (servingsPerBottle * bottleCount)
     * Totals from existing badge attrs (data-sns-price / data-onetime-price).
     */
    function updateMultibuyValuePrices() {
      var root = getOverrideRoot();
      var isSub = isSubscriptionSelected();

      root.querySelectorAll(SEL.tileBadge).forEach(function (badge) {
        var label = badge.closest('label');
        if (!label) return;

        var perUnitEl = label.querySelector('[data-tw-mb-per-unit]');
        var perServingEl = label.querySelector('[data-tw-mb-per-serving]');
        if (!perUnitEl && !perServingEl) return;

        var qty = parseInt(badge.getAttribute('data-option-index') || '1', 10) || 1;
        var total = String(
          isSub
            ? (badge.getAttribute('data-sns-price') || '')
            : (badge.getAttribute('data-onetime-price') || '')
        )
          .replace(/\s+[A-Za-z]{3}\s*$/i, '')
          .trim();
        var match = total.match(/^([^0-9]*)([\d.,]+)/);
        if (!match) {
          if (perUnitEl) perUnitEl.textContent = '';
          if (perServingEl) perServingEl.textContent = '';
          return;
        }

        var amount = parseFloat(match[2].replace(',', '.'));
        if (!amount) {
          if (perUnitEl) perUnitEl.textContent = '';
          if (perServingEl) perServingEl.textContent = '';
          return;
        }

        if (perUnitEl) {
          var containerType =
            perUnitEl.getAttribute('data-container-type') ||
            (label.querySelector('[data-tw-mb-tile-data]') &&
              label
                .querySelector('[data-tw-mb-tile-data]')
                .getAttribute('data-container-type')) ||
            'bottle';
          perUnitEl.textContent =
            match[1] + (amount / qty).toFixed(2) + '/' + containerType;
        }

        if (perServingEl) {
          // Order: day supply → explicit serving count → ovule count →
          // serving_per_bottle × pack.
          var dataEl = label.querySelector('[data-tw-mb-tile-data]') || badge;
          var supplyDays = parseInt(
            dataEl.getAttribute('data-supply-days') || '0',
            10
          );
          var servingCount = parseInt(
            dataEl.getAttribute('data-serving-count') || '0',
            10
          );
          var snippetUnits = parseInt(
            dataEl.getAttribute('data-snippet-unit-count') || '0',
            10
          );
          var unitServings = parseInt(
            dataEl.getAttribute('data-servings-per-bottle') || '0',
            10
          );
          var totalServings = 0;
          if (supplyDays > 0) {
            totalServings = supplyDays;
          } else if (servingCount > 0) {
            totalServings = servingCount;
          } else if (snippetUnits > 0) {
            totalServings = snippetUnits;
          } else if (unitServings > 0) {
            totalServings = unitServings * qty;
          }
          if (totalServings > 0) {
            var servingLabel =
              dataEl.getAttribute('data-serving-label') || 'serving';
            perServingEl.textContent =
              match[1] +
              (amount / totalServings).toFixed(2) +
              '/' +
              servingLabel;
          } else {
            perServingEl.textContent = '';
          }
        }
      });
    }
  
    /** Switches tile badge text between subscription and one-time savings */
    function updateTileBadges() {
      var root = getOverrideRoot();
      var isSub = isSubscriptionSelected();
      root.querySelectorAll(SEL.tileBadge).forEach(function (badge) {
        var pct = isSub ? (badge.getAttribute('data-save-subscription') || '') : (badge.getAttribute('data-save-onetime') || '');
        if (pct) {
          badge.textContent = 'SAVE ' + pct + '%';
          badge.style.display = '';
        } else {
          badge.textContent = '';
          badge.style.display = 'none';
        }
      });
      updateMultibuyValuePrices();
    }

    /* ─── Recharge widget overrides ───────────────────────────────────────── */
  
    function applyOverrides() {
      if (_busy) return;
  
      var widget = getOverrideRoot().querySelector(SEL.widget);
      if (!widget || !widget.shadowRoot) return;

      var shadow  = widget.shadowRoot;
      var badge   = getActiveBadge();
      var savePct = badge ? (badge.getAttribute('data-save-subscription') || '') : '';
  
      _busy = true;
  
      // 1. Subscription: compare line = full stack (unit one-time × qty)
      (function setSubscriptionCompare() {
        var subRoot = getPurchaseOptionByPart(shadow, 'subscription');
        var el = subRoot
          ? (subRoot.querySelector(SEL.strikethrough) ||
            findByPart(subRoot, 'rc-purchase-option__original-price'))
          : null;
        if (!el) {
          el = shadow.querySelector(SEL.strikethrough);
        }
        if (el) {
          setLitText(
            el,
            badge ? formatCompareAtMoney(badge) : null
          );
        }
      }());

      // 2. One-time row: show same compare above one-time when tier has savings
      applyOnetimeComparePrice(shadow, badge);
  
      // 3. Widget badge % → stacked saving (e.g. "Save 14.5%")
      setLitText(
        shadow.querySelector(SEL.rcBadge),
        savePct ? 'Save ' + savePct + '%' : null
      );
  
      // 4. Deliver every — rc-selling-plans has its own nested shadow root.
      //    Only replaces dropdown when exactly one plan option exists.
      //    Guard: if no select found (button-group or list display), does nothing.
      var pShadow = nestedShadow(shadow, SEL.plans);
      if (pShadow) {
        var select = pShadow.querySelector(SEL.plansSelect);
        if (select && select.options.length === 1) {
          // Re-inject hide CSS every time — Lit wipes rc-selling-plans shadow
          // on subscription/one-time toggle, removing previously injected styles.
          if (!pShadow.querySelector('.' + COPY.plansStyle)) {
            var style = document.createElement('style');
            style.className = COPY.plansStyle;
            style.textContent = SEL.plansSelect + ',' + SEL.plansIcon + '{display:none!important}';
            pShadow.appendChild(style);
          }
          // Re-read interval from select each time — label may have been wiped by Lit.
          var lbl = pShadow.querySelector(SEL.plansLabel);
          if (lbl) {
            var interval = select.options[0].text.split(':')[0].trim();
            lbl.setAttribute('data-interval', interval);
            setDeliverLabelWithStyledValue(
              pShadow,
              lbl,
              interval,
              savePct
            );
          }
        }
      }
  
      _busy = false;
    }
  
    /* ─── Observer ────────────────────────────────────────────────────────── */
  
    /**
     * Targeted MutationObserver — watches only the elements we override.
     *
     * Two observation scopes:
     * 1. Shadow root (childList + subtree) — catches full Lit re-renders including
     *    nested custom elements (rc-selling-plans, rc-benefits) that get recreated
     *    when Recharge toggles subscription/one-time.
     * 2. Specific override targets (childList + subtree + characterData) —
     *    catches Lit reverting our nodeValue changes on those elements only,
     *    avoiding the cost of characterData on the entire shadow tree.
     */
    function setupObserver(shadow) {
      if (_observer) _observer.disconnect();
  
      _observer = new MutationObserver(function () {
        if (_busy) return;
        clearTimeout(_reapplyTimer);
        _reapplyTimer = setTimeout(applyOverrides, 80);
      });
  
      // Full shadow tree — catches nested custom element re-renders on radio toggle
      _observer.observe(shadow, { childList: true, subtree: true });
  
      // Text / children on the elements we override (all strikethrough lines)
      var subOpts = { childList: true, subtree: true, characterData: true };
      var b = shadow.querySelector(SEL.rcBadge);
      if (b) _observer.observe(b, subOpts);
      shadow.querySelectorAll(SEL.strikethrough).forEach(function (el) {
        _observer.observe(el, subOpts);
      });
      var oneRoot = getOnetimeBlock(shadow);
      if (oneRoot) {
        var onePr = findByPart(
          oneRoot,
          'rc-purchase-option__prices',
        ) || oneRoot.querySelector('[part*="prices"]');
        if (onePr) _observer.observe(onePr, subOpts);
        var oneCmp = oneRoot.querySelector('span.tw-rc-onetime-compare');
        if (oneCmp) _observer.observe(oneCmp, subOpts);
      }
    }
  
    /* ─── Widget init ─────────────────────────────────────────────────────── */

    function initWidget(root) {
      root = root || document;
      _scopedRoot = root === document ? null : root;

      // Quick-buy injects tile markup after page boot — fill €/bottle + €/serving
      // immediately (and again shortly after Recharge settles purchase option).
      updateTileBadges();
      [120, 400].forEach(function (ms) {
        setTimeout(updateTileBadges, ms);
      });

      var widget = root.querySelector(SEL.widget);
      if (!widget) return;

      function tryStart() {
        if (!widget.shadowRoot || !widget.shadowRoot.querySelector('.rc-selection__root')) return false;
        applyOverrides();
        updateTileBadges();
        setupObserver(widget.shadowRoot);
        return true;
      }

      if (!tryStart()) {
        var waitObs = new MutationObserver(function (_, obs) {
          if (tryStart()) obs.disconnect();
        });
        waitObs.observe(widget.shadowRoot || widget, { childList: true, subtree: true });
        [800, 2000].forEach(function (ms) { setTimeout(tryStart, ms); });
      }
    }

    window.twRechargeInitWidget = initWidget;
  
    /* ─── Event wiring ────────────────────────────────────────────────────── */
  
    function initEvents() {
      // Subscription / bundleapp plan toggle → update tile badges
      document.addEventListener('change', function (e) {
        if (!e.target) return;
        if (e.target.name === 'bundleapp-plan-selector-group' || e.target.name === 'selling_plan') {
          updateTileBadges();
          setTimeout(applyOverrides, 80);
        }
      });
  
      // Clicks inside Recharge widget or bundleapp → update tile badges + re-apply
      // overrides after Lit finishes re-rendering on subscription/one-time toggle.
      // 350ms gives Recharge time to complete its state update before we override.
      document.addEventListener('click', function (e) {
        if (!e.target) return;
        if (e.target.closest(SEL.bundleWrap) || e.target.closest(SEL.widget)) {
          setTimeout(updateTileBadges, 80);
          setTimeout(applyOverrides, 350);
        }
      });
  
      // Quantity tile radio change → re-apply overrides + notify Recharge of variant change
      document.addEventListener('change', function (e) {
        if (!e.target || !e.target.closest) return;
        if (
          e.target.closest(SEL.tileList) &&
          e.target.name !== 'bundleapp-plan-selector-group' &&
          e.target.name !== 'selling_plan' &&
          e.target.name !== 'rc_plan'
        ) {
          updateTileBadges();
          setTimeout(applyOverrides, 80);
          // Dispatch change on variant ID input so Recharge re-renders its own prices natively
          setTimeout(function () {
            var formEl = e.target.closest('form');
            var idInput = formEl
              ? formEl.querySelector('input[name="id"]')
              : null;
            if (idInput)
              idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }, 50);
        }
      });
  
      // BundleApp ready event
      document.addEventListener('bundleapp:ready', function () {
        setTimeout(updateTileBadges, 700);
        setTimeout(applyOverrides, 900);
      });
  
      // Initial / retry: tiles exist without BundleApp wrap on many PDPs
      if (document.querySelector(SEL.tileBadge)) {
        setTimeout(updateTileBadges, 200);
        setTimeout(updateTileBadges, 800);
        setTimeout(updateTileBadges, 1500);
      }
    }
  
    /* ─── Boot ────────────────────────────────────────────────────────────── */
  
    function boot() {
      initEvents();
      initWidget();
      updateMultibuyValuePrices();
      updateTileBadges();
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  
  })();